import moment from "moment";

/**
 * Single source of truth for DISPLAY date formatting (audit UX-05).
 *
 * Components must NOT call `moment(...).format("...")` ad-hoc for display —
 * pick a semantic token here so dates read consistently everywhere. Storage /
 * API values stay as raw "YYYY-MM-DD" (that is NOT display and is unaffected).
 *
 * Tokens map to one canonical human format each (no leading zeros — the audit
 * explicitly flags "Jun 03" vs "Jun 3" drift):
 *
 *   short        → "Jun 3"
 *   long         → "Jun 3, 2026"
 *   numeric      → "6/3/2026"
 *   monthYear    → "Jun 2026"
 *   weekday      → "Mon, Jun 3"
 *   weekdayLong  → "Monday, June 3"
 *   time         → "3:06 AM"
 *   dateTime     → "Jun 3, 3:06 AM"
 *
 * Implementation detail: moment is used internally because it is already the
 * project's de-facto date lib (65+ files). Call sites depend only on the token
 * API, so the engine can be swapped later without touching them.
 */
export type DateFormatToken =
  | "short"
  | "long"
  | "numeric"
  | "monthYear"
  | "weekday"
  | "weekdayLong"
  | "time"
  | "dateTime";

export type DateInput = string | number | Date | moment.Moment | null | undefined;

const TOKEN_FORMATS: Record<DateFormatToken, string> = {
  short: "MMM D",
  long: "MMM D, YYYY",
  numeric: "M/D/YYYY",
  monthYear: "MMM YYYY",
  weekday: "ddd, MMM D",
  weekdayLong: "dddd, MMMM D",
  time: "h:mm A",
  dateTime: "MMM D, h:mm A",
};

/**
 * Parse any accepted input into a valid moment, or null.
 *
 * Date-only "YYYY-MM-DD" strings are parsed with an explicit format so they
 * resolve to LOCAL midnight (Safari-safe and avoids the UTC off-by-one day
 * that `new Date("YYYY-MM-DD")` / loose parsing can introduce in negative
 * timezones). Everything else goes through moment's normal parser.
 */
function toMoment(value: DateInput): moment.Moment | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const m = moment(value, "YYYY-MM-DD", true);
    return m.isValid() ? m : null;
  }
  const m = moment(value);
  return m.isValid() ? m : null;
}

/**
 * Format a single date for display.
 * Returns "" for empty/invalid input so callers can render a clean blank.
 */
export function formatDate(value: DateInput, token: DateFormatToken = "long"): string {
  const m = toMoment(value);
  if (!m) return "";
  return m.format(TOKEN_FORMATS[token]);
}

/**
 * Format a date range with smart collapsing so the shared parts aren't
 * repeated:
 *   - same day            → "Jun 3, 2026"            (single date)
 *   - same month + year   → "Jun 3–15, 2026"
 *   - same year           → "Jun 3 – Jul 15, 2026"
 *   - different years     → "Jun 3, 2025 – Jul 15, 2026"
 *
 * `variant: "short"` drops the year entirely ("Jun 3 – Jul 15").
 * The en dash (–) is the correct typographic range separator.
 */
export function formatDateRange(
  start: DateInput,
  end: DateInput,
  variant: "short" | "long" = "long",
): string {
  const a = toMoment(start);
  const b = toMoment(end);

  // Degrade gracefully if one side is missing.
  if (!a && !b) return "";
  if (!a) return formatDate(end, variant === "short" ? "short" : "long");
  if (!b) return formatDate(start, variant === "short" ? "short" : "long");

  if (a.isSame(b, "day")) {
    return formatDate(start, variant === "short" ? "short" : "long");
  }

  const sameYear = a.year() === b.year();
  const sameMonth = sameYear && a.month() === b.month();

  if (variant === "short") {
    if (sameMonth) return `${a.format("MMM D")}–${b.format("D")}`;
    return `${a.format("MMM D")} – ${b.format("MMM D")}`;
  }

  if (sameMonth) return `${a.format("MMM D")}–${b.format("D")}, ${b.format("YYYY")}`;
  if (sameYear) return `${a.format("MMM D")} – ${b.format("MMM D")}, ${b.format("YYYY")}`;
  return `${a.format("MMM D, YYYY")} – ${b.format("MMM D, YYYY")}`;
}
